/* global BigInt */
import React, { useEffect, useRef } from 'react';

import RoundedButton from 'components/RoundedButton/RoundedButton';

import styles from './BillingCapacityMove.module.scss';

/* eslint-disable max-len -- Keep localized financial notices together. */
export const purchaseLabels = {
  en: {
    context: 'PURCHASED SEAT UPDATE',
    title: 'Review purchased seats',
    current: 'Current',
    after: 'After update',
    seats: 'Purchased active seats',
    rate: 'Unit rate',
    unchanged: '✓ Unchanged',
    subtotal: 'Recurring seat subtotal',
    exclusions: 'Before taxes, discounts and add-ons.',
    proration: 'Any prorated charge is calculated by your billing provider using existing billing settings.',
    back: 'Back',
    confirm: 'Confirm update',
    subscription: 'Subscription',
    review: 'Review purchase',
    cancel: 'Cancel',
    units: 'number of units',
    unitPrice: 'Unit price',
    plan: 'Package',
    currency: 'Currency',
    invalid: 'Enter a whole number of seats at least equal to the current purchased quantity.',
    unavailable: 'A fresh billing preview is required to review this purchase.',
    refresh: 'Refresh preview',
    sending: 'Updating purchased seats…',
    success: 'Purchased seats updated.',
    failed: 'The purchase outcome could not be confirmed. Retry with the same request.',
    retry: 'Retry update',
    stale: 'The subscription has changed. Refresh the preview before reviewing this purchase.',
    pending: 'Purchase pending — reconciliation required.',
    conflict: 'This purchase request cannot be reused. Refresh the preview.',
    rejected: 'The update was rejected. Refresh the preview to review current seats.',
    month: 'month',
    year: 'year',
    week: 'week',
    day: 'day',
  },
  ar: {
    context: 'تحديث المقاعد المشتراة',
    title: 'مراجعة المقاعد المشتراة',
    current: 'الحالي',
    after: 'بعد التحديث',
    seats: 'المقاعد النشطة المشتراة',
    rate: 'سعر الوحدة',
    unchanged: '✓ دون تغيير',
    subtotal: 'المجموع الفرعي المتكرر للمقاعد',
    exclusions: 'قبل الضرائب والخصومات والإضافات.',
    proration: 'يحسب مزود الفوترة أي رسوم نسبية وفقًا لإعدادات الفوترة الحالية.',
    back: 'رجوع',
    confirm: 'تأكيد التحديث',
    subscription: 'الاشتراك',
    review: 'مراجعة الشراء',
    cancel: 'إلغاء',
    units: 'عدد المقاعد',
    unitPrice: 'سعر الوحدة',
    plan: 'الباقة',
    currency: 'العملة',
    invalid: 'أدخل عددًا صحيحًا من المقاعد لا يقل عن العدد المشترى حاليًا.',
    unavailable: 'يلزم الحصول على معاينة فوترة حديثة لمراجعة هذا الشراء.',
    refresh: 'تحديث المعاينة',
    sending: 'جارٍ تحديث المقاعد المشتراة…',
    success: 'تم تحديث المقاعد المشتراة.',
    failed: 'تعذر تأكيد نتيجة الشراء. أعد المحاولة بالطلب نفسه.',
    retry: 'إعادة محاولة التحديث',
    stale: 'تغير الاشتراك. حدّث المعاينة قبل مراجعة هذا الشراء.',
    pending: 'الشراء معلق — يلزم التحقق من التسوية.',
    conflict: 'لا يمكن إعادة استخدام طلب الشراء هذا. حدّث المعاينة.',
    rejected: 'رُفض التحديث. حدّث المعاينة لمراجعة المقاعد الحالية.',
    month: 'شهر',
    year: 'سنة',
    week: 'أسبوع',
    day: 'يوم',
  },
};

// Decimal-string arithmetic preserves a server-approved unit rate, including sub-cent precision.
export const seatSubtotal = (rate, quantity) => {
  if (!/^\d+(?:\.\d+)?$/.test(String(rate)) || !Number.isSafeInteger(Number(quantity)) || Number(quantity) < 0)
    return null;
  const [whole, fraction = ''] = String(rate).split('.');
  const digits = (BigInt(whole + fraction) * BigInt(quantity)).toString().padStart(fraction.length + 1, '0');
  return fraction.length ? `${digits.slice(0, -fraction.length)}.${digits.slice(-fraction.length)}` : digits;
};

export const purchaseMoney = (value, rates) => {
  if (value == null || !rates?.currency) return '—';
  const [whole, rawFraction = ''] = String(value).split('.');
  const fraction = rawFraction.replace(/0+$/, '');
  const decimals = Number.isInteger(rates.currencyExponent) ? fraction.padEnd(rates.currencyExponent, '0') : fraction;
  return `${rates.currency} ${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${decimals ? `.${decimals}` : ''}`;
};

export const hasPurchaseContext = (preview, selected) =>
  !!(
    preview?.previewId &&
    (preview.status === 'blocked' ||
      (preview.status === 'pending' &&
        preview.mode === 'setup_pending' &&
        preview.setupRecovery?.intentId &&
        preview.setupRecovery?.recoveryRevision &&
        preview.setupRecovery?.action === 'edit_purchased_seats')) &&
    preview.reasons?.length === 1 &&
    preview.reasons[0] === 'BILLING_CAPACITY_SHORTFALL' &&
    selected?.isPrimary &&
    selected.pricingModel === 'PER_UNIT' &&
    String(preview.target?.subscriptionId) === String(selected.id) &&
    preview.rates?.currency &&
    ['day', 'week', 'month', 'year'].includes(preview.rates?.period?.unit) &&
    Number.isSafeInteger(preview.rates?.period?.count) &&
    preview.rates.period.count > 0 &&
    preview.purchased?.active != null &&
    seatSubtotal(preview.rates?.active, preview.purchased.active) != null
  );

export default function PurchasedSeatsReview({
  preview,
  plan,
  quantity,
  language,
  phase,
  onBack,
  onRefresh,
  onConfirm,
}) {
  const copy = purchaseLabels[language];
  const heading = useRef(null);
  useEffect(() => {
    heading.current?.focus();
  }, []);
  const period = `${preview.rates.period.count} ${copy[preview.rates.period.unit]}`;
  const money = (value) => <bdi dir='ltr'>{purchaseMoney(value, preview.rates)}</bdi>;
  return (
    <div className={styles.review} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div className={styles.context}>{copy.context}</div>
      <h2 ref={heading} tabIndex={-1}>
        {copy.title}
      </h2>
      <p>
        {preview.target.tenantName} · {plan}
      </p>
      <table>
        <thead>
          <tr>
            <th scope='col'>{copy.seats}</th>
            <th scope='col'>{copy.current}</th>
            <th scope='col'>{copy.after}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{copy.seats}</td>
            <td>
              <bdi dir='ltr'>{preview.purchased.active}</bdi>
            </td>
            <td>
              <strong>
                <bdi dir='ltr'>{quantity}</bdi>
              </strong>
            </td>
          </tr>
        </tbody>
      </table>
      <h3>
        {copy.rate} <small style={{ color: '#087659', marginInlineStart: 12 }}>{copy.unchanged}</small>
      </h3>
      <p>
        {money(preview.rates.active)} / {period}
      </p>
      <h3>{copy.subtotal}</h3>
      <div className={styles.rates}>
        {[
          [copy.current, preview.purchased.active],
          [copy.after, quantity],
        ].map(([label, units]) => (
          <div key={label}>
            <small>{label}</small>
            <strong>{money(seatSubtotal(preview.rates.active, units))}</strong>
            <small>/ {period}</small>
          </div>
        ))}
      </div>
      <p style={{ marginTop: 16, color: '#577180', fontSize: 12 }}>{copy.exclusions}</p>
      <div className={styles.timing}>{copy.proration}</div>
      {phase !== 'review' && (
        <p role={['failed', 'stale', 'rejected', 'conflict'].includes(phase) ? 'alert' : 'status'}>{copy[phase]}</p>
      )}
      <footer>
        <RoundedButton
          text={phase === 'review' || phase === 'sending' ? copy.back : copy.refresh}
          color='white'
          isDisabled={phase === 'sending'}
          onClick={phase === 'review' ? onBack : onRefresh}
        />
        {['review', 'failed', 'sending'].includes(phase) && (
          <RoundedButton
            text={phase === 'sending' ? copy.sending : phase === 'failed' ? copy.retry : copy.confirm}
            color='primary'
            isDisabled={phase === 'sending'}
            onClick={onConfirm}
          />
        )}
      </footer>
    </div>
  );
}
